import { toast } from "react-toastify";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getAdminDashboardSummary } from "../../shared/api/dashboardService";

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 0,
  })}`;

const number = (value) => Number(value || 0).toLocaleString("en-IN");
const pct = (value) => `${Number(value || 0).toFixed(2)}%`;

const dateText = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const shortName = (value, max = 22) => {
  const text = String(value || "N/A");
  return text.length > max ? `${text.slice(0, max)}...` : text;
};

const escapeHtml = (value) =>
  String(value ?? "—")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const tableHtml = (title, headers, rows) => {
  const body = rows && rows.length
    ? rows.map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`).join("")
    : `<tr><td class="empty" colspan="${headers.length}">No data available</td></tr>`;
  return `
    <section class="section">
      <h2>${escapeHtml(title)}</h2>
      <table>
        <thead><tr>${headers.map((h) => `<th>${escapeHtml(h)}</th>`).join("")}</tr></thead>
        <tbody>${body}</tbody>
      </table>
    </section>`;
};

function openDashboardPdf(summaryData, period, batches) {
  const dashboard = summaryData || {};
  const summary = dashboard.summary || {};
  const health = dashboard.health || {};
  const financial = dashboard.financial || {};
  const inventory = dashboard.inventory || {};
  const investor = dashboard.investor || {};
  const weight = dashboard.weight || {};
  const todayTasks = dashboard.todayTasks || [];
  const alerts = dashboard.alerts || [];
  const recentActivities = dashboard.recentActivities || [];
  const generatedAt = new Date().toLocaleString("en-IN");

  const sections = [
    tableHtml("Main KPI Summary", ["Metric", "Value"], [
      ["Total Livestock", number(summary.totalLivestock)],
      ["Available Livestock", number(summary.availableLivestock)],
      ["Sold Livestock", number(summary.soldLivestock)],
      ["Total Batches", number(summary.totalBatches)],
      ["Active Batches", number(summary.activeBatches)],
      ["Investment", money(summary.totalInvestment)],
      ["Revenue", money(summary.totalRevenue)],
      ["Profit / Loss", money(summary.totalProfit)],
      ["ROI", pct(summary.roi)],
      ["Pending Tasks Today", number(summary.pendingTasksToday)],
      ["Open Disease Cases", number(summary.openDiseaseCases)],
      ["Low Stock Count", number(summary.lowStockCount)],
    ]),
    tableHtml(`Batch Performance - ${period}`, ["Batch", "Goats", "Investment", "Revenue", "Profit", "ROI", "Status"],
      (batches || []).map((b) => [b.batchName || b.name || `Batch ${b.id}`, number(b.goats || b.totalCount || b.livestockCount), money(b.investment), money(b.revenue), money(b.profit), pct(b.roi), b.status || b.period || "-"])
    ),
    tableHtml("Health & Treatment", ["Metric", "Value"], [
      ["Healthy", number(health.healthy)],
      ["Sick", number(health.sick)],
      ["Under Treatment", number(health.underTreatment)],
      ["Open Disease Cases", number(health.openDiseaseCases)],
      ["Medicine Due Today", number(health.medicineDueToday)],
      ["Vaccine Due This Week", number(health.vaccineDueThisWeek)],
    ]),
    tableHtml("Financial Overview", ["Metric", "Value"], [
      ["Livestock Cost", money(financial.livestockCost)],
      ["Labour Cost", money(financial.labourCost)],
      ["Total Expenses", money(financial.totalExpenses)],
      ["Sales", money(financial.sales)],
      ["Net Profit", money(financial.netProfit)],
      ["ROI", pct(financial.roi)],
    ]),
    tableHtml("Weight Growth", ["Metric", "Value"], [
      ["Average Current Weight", `${Number(weight.averageCurrentWeight || 0).toFixed(1)} kg`],
      ["Total Weight Gain", `${Number(weight.totalWeightGain || 0).toFixed(1)} kg`],
      ["Weight Not Updated", number(weight.weightNotUpdated)],
      ["Best Growth Animal", weight.bestGrowthAnimal?.assetId || weight.bestGrowthAnimal?.name || "-"],
      ["Best Gain", `${Number(weight.bestGrowthAnimal?.gain || 0).toFixed(1)} kg`],
    ]),
    tableHtml("Inventory", ["Item", "Type", "Qty", "Unit", "Status"],
      (inventory.items || []).map((i) => [i.itemName || i.name || "-", i.type || i.itemType || "-", number(i.quantity), i.unit || "", i.status || (i.lowStockAlert ? "Low Stock" : "Good")])
    ),
    tableHtml("Investor Overview", ["Metric", "Value"], [
      ["Total Investors", number(investor.totalInvestors)],
      ["Active Investments", number(investor.activeInvestments)],
      ["Investor Amount", money(investor.totalInvestorAmount)],
      ["Pending Payout", money(investor.pendingPayout)],
    ]),
    tableHtml("Today Work", ["Type", "Title", "Batch", "Asset", "Status"],
      todayTasks.map((t) => [t.type || "-", t.title || t.itemName || "-", t.batchName || "-", t.asset || t.assetId || "-", t.status || "-"])
    ),
    tableHtml("Priority Alerts", ["Title", "Message", "Severity"],
      alerts.map((a) => [a.title || "Alert", a.message || a.description || "-", a.severity || a.type || "-"])
    ),
    tableHtml("Recent Activities", ["Title", "Description", "Time"],
      recentActivities.map((a) => [a.title || "-", a.description || "-", dateText(a.eventTime)])
    ),
  ].join("");

  const html = `<!doctype html><html><head><meta charset="utf-8" />
    <title>Farm Admin Dashboard PDF</title>
    <style>
      @page { size: A4 landscape; margin: 12mm; }
      body { font-family: Arial, sans-serif; margin: 0; color: #0f172a; background: #f8fafc; }
      .cover { background: linear-gradient(135deg,#4f46e5,#0891b2,#16a34a); color: white; padding: 26px; border-radius: 18px; margin-bottom: 18px; }
      h1 { margin: 0; font-size: 30px; }
      .subtitle { margin-top: 8px; opacity: .9; }
      .chips { display: flex; gap: 10px; flex-wrap: wrap; margin-top: 14px; }
      .chip { border: 1px solid rgba(255,255,255,.35); background: rgba(255,255,255,.18); padding: 7px 12px; border-radius: 999px; font-size: 12px; font-weight: 700; }
      .section { background: white; border: 1px solid #dbeafe; border-radius: 16px; padding: 15px; margin-bottom: 15px; box-shadow: 0 10px 26px rgba(15,23,42,.08); break-inside: avoid; }
      h2 { margin: 0 0 12px; color: #1e293b; font-size: 18px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #1e293b; color: white; text-align: left; padding: 10px; font-size: 11px; text-transform: uppercase; }
      td { padding: 9px 10px; border-bottom: 1px solid #e2e8f0; font-size: 12px; }
      tr:nth-child(even) td { background: #f1f5f9; }
      .empty { text-align: center; color: #64748b; padding: 22px; }
      .footer { color: #64748b; font-size: 11px; text-align: center; margin-top: 18px; }
      @media print { body { background: white; } }
    </style>
  </head><body>
    <div class="cover"><h1>Farm Admin Dashboard Report</h1><div class="subtitle">Complete farm summary with batches, finance, health, stock, investor and task data.</div><div class="chips"><div class="chip">Period: ${escapeHtml(period)}</div><div class="chip">Generated: ${escapeHtml(generatedAt)}</div><div class="chip">AgriWealth Farm OS</div></div></div>
    ${sections}
    <div class="footer">Generated from AgriWealth Farm OS Admin Dashboard.</div>
    <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 350); };</script>
  </body></html>`;

  const win = window.open("", "_blank");
  if (!win) {
    toast.error("Please allow popups to export the dashboard PDF.");
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}


const Styles = () => (
  <style>{`
    .dash-wrap {
      min-height: 100vh;
      color: #f8fafc;
    }

    .dash-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;
      flex-wrap: wrap;
      margin-bottom: 22px;
    }

    .dash-title {
      margin: 0;
      font-size: 30px;
      font-weight: 900;
      letter-spacing: -0.04em;
      background: linear-gradient(135deg, #ffffff 0%, #a5b4fc 45%, #67e8f9 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .dash-subtitle {
      margin: 7px 0 0;
      color: rgba(255,255,255,0.46);
      font-size: 13px;
      line-height: 1.6;
    }

    .dash-actions {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .dash-btn {
      border: 1px solid rgba(255,255,255,0.09);
      background: rgba(255,255,255,0.05);
      color: rgba(255,255,255,0.86);
      border-radius: 12px;
      padding: 10px 13px;
      font-size: 12px;
      font-weight: 800;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      transition: 0.18s ease;
      text-decoration: none;
    }

    .dash-btn:hover {
      transform: translateY(-1px);
      background: rgba(255,255,255,0.08);
      border-color: rgba(255,255,255,0.16);
    }

    .dash-btn.primary {
      background: linear-gradient(135deg, #6366f1, #8b5cf6);
      border-color: rgba(139,92,246,0.58);
      color: white;
      box-shadow: 0 12px 30px rgba(99,102,241,0.24);
    }

    .kpi-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 14px;
      margin-bottom: 18px;
    }

    .kpi-card,
    .dash-panel,
    .mini-card {
      background: linear-gradient(145deg, rgba(30,37,54,0.98), rgba(17,24,39,0.96));
      border: 1px solid rgba(255,255,255,0.075);
      border-radius: 18px;
      box-shadow: 0 18px 42px rgba(0,0,0,0.22);
    }

    .kpi-card {
      padding: 17px;
      position: relative;
      overflow: hidden;
    }

    .kpi-card::after {
      content: "";
      position: absolute;
      inset: auto -35px -40px auto;
      width: 120px;
      height: 120px;
      border-radius: 50%;
      background: var(--glow, rgba(99,102,241,0.14));
      filter: blur(14px);
    }

    .kpi-top {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: center;
      position: relative;
      z-index: 1;
    }

    .kpi-icon {
      width: 42px;
      height: 42px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 18px;
      background: var(--accent-bg, rgba(99,102,241,0.14));
      border: 1px solid var(--accent-border, rgba(99,102,241,0.24));
    }

    .kpi-label {
      margin: 0;
      color: rgba(255,255,255,0.44);
      font-size: 11px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }

    .kpi-value {
      margin: 12px 0 0;
      color: white;
      font-size: 25px;
      font-weight: 900;
      letter-spacing: -0.03em;
      position: relative;
      z-index: 1;
    }

    .kpi-note {
      margin: 6px 0 0;
      color: rgba(255,255,255,0.38);
      font-size: 11.5px;
      position: relative;
      z-index: 1;
    }

    .quick-grid {
      display: grid;
      grid-template-columns: repeat(6, minmax(0, 1fr));
      gap: 10px;
      margin-bottom: 18px;
    }

    .quick-card {
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(255,255,255,0.035);
      border-radius: 15px;
      padding: 14px;
      color: rgba(255,255,255,0.84);
      cursor: pointer;
      text-align: left;
      transition: 0.18s ease;
      min-height: 86px;
    }

    .quick-card:hover {
      background: rgba(99,102,241,0.13);
      border-color: rgba(129,140,248,0.28);
      transform: translateY(-1px);
    }

    .quick-icon { font-size: 20px; margin-bottom: 8px; }
    .quick-title { font-size: 12px; font-weight: 900; margin: 0; }
    .quick-subtitle { font-size: 10.5px; color: rgba(255,255,255,0.36); margin: 5px 0 0; line-height: 1.45; }

    .dash-grid-2 {
      display: grid;
      grid-template-columns: minmax(0, 1.45fr) minmax(340px, 0.8fr);
      gap: 16px;
      margin-bottom: 16px;
    }

    .dash-grid-3 {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
      margin-bottom: 16px;
    }

    .dash-panel {
      padding: 18px;
      overflow: hidden;
    }

    .panel-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      margin-bottom: 14px;
    }

    .panel-title {
      margin: 0;
      color: white;
      font-size: 17px;
      font-weight: 900;
      letter-spacing: -0.02em;
    }

    .panel-subtitle {
      margin: 4px 0 0;
      color: rgba(255,255,255,0.38);
      font-size: 11.5px;
    }

    .period-tabs {
      display: flex;
      gap: 7px;
      padding: 5px;
      background: rgba(255,255,255,0.035);
      border: 1px solid rgba(255,255,255,0.06);
      border-radius: 13px;
    }

    .period-tab {
      border: none;
      border-radius: 10px;
      background: transparent;
      color: rgba(255,255,255,0.45);
      padding: 7px 10px;
      font-size: 11px;
      font-weight: 900;
      cursor: pointer;
    }

    .period-tab.active {
      background: linear-gradient(135deg,#6366f1,#8b5cf6);
      color: white;
      box-shadow: 0 10px 24px rgba(99,102,241,0.22);
    }

    .alert-stack {
      display: grid;
      gap: 10px;
    }

    .alert-card {
      padding: 12px;
      border-radius: 14px;
      border: 1px solid rgba(255,255,255,0.08);
      background: rgba(255,255,255,0.035);
      display: flex;
      gap: 10px;
      align-items: flex-start;
      cursor: pointer;
      transition: 0.18s ease;
    }

    .alert-card:hover { background: rgba(255,255,255,0.06); }
    .alert-dot { width: 9px; height: 9px; border-radius: 50%; margin-top: 5px; flex: 0 0 auto; }
    .alert-title { margin: 0; color: white; font-size: 12.5px; font-weight: 900; }
    .alert-msg { margin: 4px 0 0; color: rgba(255,255,255,0.45); font-size: 11.5px; line-height: 1.45; }

    .data-table {
      width: 100%;
      border-collapse: collapse;
      min-width: 760px;
    }

    .data-table th,
    .data-table td {
      padding: 12px 12px;
      border-bottom: 1px solid rgba(255,255,255,0.055);
      text-align: left;
      font-size: 12px;
      color: rgba(255,255,255,0.76);
      white-space: nowrap;
    }

    .data-table th {
      color: rgba(255,255,255,0.34);
      text-transform: uppercase;
      letter-spacing: 0.07em;
      font-size: 10px;
      font-weight: 900;
    }

    .table-scroll { overflow-x: auto; }

    .status-pill {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 5px 9px;
      border-radius: 999px;
      font-size: 10.5px;
      font-weight: 900;
      border: 1px solid rgba(255,255,255,0.08);
    }

    .progress-track {
      width: 115px;
      height: 7px;
      border-radius: 999px;
      background: rgba(255,255,255,0.07);
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      border-radius: 999px;
      background: linear-gradient(90deg,#6366f1,#22c55e);
    }

    .mini-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }

    .mini-card {
      padding: 14px;
    }

    .mini-label {
      color: rgba(255,255,255,0.38);
      font-size: 10.5px;
      font-weight: 900;
      text-transform: uppercase;
      letter-spacing: 0.07em;
      margin: 0;
    }

    .mini-value {
      margin: 8px 0 0;
      color: #fff;
      font-size: 20px;
      font-weight: 900;
    }

    .chart-box { height: 310px; }

    .list-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 0;
      border-bottom: 1px solid rgba(255,255,255,0.06);
    }

    .list-row:last-child { border-bottom: none; }
    .list-title { margin: 0; color: rgba(255,255,255,0.86); font-size: 12.5px; font-weight: 900; }
    .list-sub { margin: 4px 0 0; color: rgba(255,255,255,0.38); font-size: 11.5px; line-height: 1.45; }
    .empty-state { color: rgba(255,255,255,0.38); font-size: 12px; padding: 18px 0; text-align: center; }

    .loader-panel, .error-panel {
      min-height: 360px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: rgba(255,255,255,0.56);
      gap: 14px;
    }

    .loader-ring {
      width: 46px;
      height: 46px;
      border-radius: 50%;
      border: 4px solid rgba(99,102,241,0.14);
      border-top-color: #818cf8;
      animation: dashSpin 0.9s linear infinite;
    }

    @keyframes dashSpin { to { transform: rotate(360deg); } }

    @media (max-width: 1280px) {
      .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
      .quick-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
      .dash-grid-2, .dash-grid-3 { grid-template-columns: 1fr; }
    }

    @media (max-width: 720px) {
      .kpi-grid, .quick-grid, .mini-grid { grid-template-columns: 1fr; }
      .dash-title { font-size: 24px; }
      .dash-actions { justify-content: stretch; width: 100%; }
      .dash-btn { flex: 1; justify-content: center; }
    }
  `}</style>
);

const KpiCard = ({ label, value, note, icon, color = "#6366f1" }) => (
  <div
    className="kpi-card"
    style={{
      "--glow": `${color}26`,
      "--accent-bg": `${color}20`,
      "--accent-border": `${color}55`,
    }}
  >
    <div className="kpi-top">
      <p className="kpi-label">{label}</p>
      <div className="kpi-icon">{icon}</div>
    </div>
    <div className="kpi-value">{value}</div>
    <p className="kpi-note">{note}</p>
  </div>
);

const MiniMetric = ({ label, value }) => (
  <div className="mini-card">
    <p className="mini-label">{label}</p>
    <p className="mini-value">{value}</p>
  </div>
);

const StatusPill = ({ value }) => {
  const text = String(value || "N/A");
  const lower = text.toLowerCase();
  let color = "#94a3b8";
  if (lower.includes("active") || lower.includes("present") || lower.includes("good") || lower.includes("healthy")) color = "#10b981";
  if (lower.includes("pending") || lower.includes("planned") || lower.includes("future") || lower.includes("upcoming")) color = "#8b5cf6";
  if (lower.includes("missed") || lower.includes("sick") || lower.includes("low") || lower.includes("high")) color = "#ef4444";
  if (lower.includes("completed") || lower.includes("past")) color = "#f59e0b";
  return (
    <span className="status-pill" style={{ color, background: `${color}18`, borderColor: `${color}35` }}>
      <span style={{ width: 6, height: 6, borderRadius: 99, background: color }} />
      {text}
    </span>
  );
};

const EmptyState = ({ text = "No data available" }) => <div className="empty-state">{text}</div>;

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("Present");

  const loadDashboard = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAdminDashboardSummary();
      setDashboard(data || {});
    } catch (err) {
      console.error("Dashboard load failed", err);
      setError(err?.response?.data?.message || "Failed to load admin dashboard. Please check backend console.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const summary = dashboard?.summary || {};
  const batchCounts = dashboard?.batchCounts || {};
  const health = dashboard?.health || {};
  const financial = dashboard?.financial || {};
  const inventory = dashboard?.inventory || {};
  const investor = dashboard?.investor || {};
  const weight = dashboard?.weight || {};
  const charts = dashboard?.charts || {};
  const allBatches = useMemo(() => dashboard?.batchPerformance || [], [dashboard?.batchPerformance]);
  const todayTasks = dashboard?.todayTasks || [];
  const alerts = dashboard?.alerts || [];
  const recentActivities = dashboard?.recentActivities || [];

  const filteredBatches = useMemo(() => {
    const wanted = period.toLowerCase();
    return allBatches.filter((batch) => String(batch.period || "Present").toLowerCase() === wanted);
  }, [allBatches, period]);

  const healthChart = charts.healthStatus || [];
  const taskChart = charts.taskStatus || [];
  const batchProfitChart = charts.batchProfit || [];

  const kpis = [
    {
      label: "Total Livestock",
      value: number(summary.totalLivestock),
      note: `${number(summary.availableLivestock)} available • ${number(summary.soldLivestock)} sold`,
      icon: "🐐",
      color: "#22c55e",
    },
    {
      label: "Active Batches",
      value: number(summary.activeBatches),
      note: `${number(summary.totalBatches)} total batches`,
      icon: "📦",
      color: "#f59e0b",
    },
    {
      label: "Investment",
      value: money(summary.totalInvestment),
      note: "Livestock purchase cost recorded",
      icon: "💰",
      color: "#6366f1",
    },
    {
      label: "Revenue",
      value: money(summary.totalRevenue),
      note: `${pct(summary.roi)} overall ROI`,
      icon: "📈",
      color: "#06b6d4",
    },
    {
      label: "Profit / Loss",
      value: money(summary.totalProfit),
      note: Number(summary.totalProfit || 0) >= 0 ? "Farm is in profit" : "Running loss until more sales",
      icon: Number(summary.totalProfit || 0) >= 0 ? "✅" : "⚠️",
      color: Number(summary.totalProfit || 0) >= 0 ? "#10b981" : "#ef4444",
    },
    {
      label: "Pending Today",
      value: number(summary.pendingTasksToday),
      note: "Medicine + vaccine tasks pending today",
      icon: "🗓️",
      color: "#8b5cf6",
    },
    {
      label: "Health Alerts",
      value: number(summary.openDiseaseCases),
      note: `${number(summary.sickAnimals)} sick / treatment animals`,
      icon: "🩺",
      color: "#fb7185",
    },
    {
      label: "Low Stock",
      value: number(summary.lowStockCount),
      note: "Items at or below threshold",
      icon: "📦",
      color: "#eab308",
    },
  ];

  const quickActions = [
    { title: "Add Batch", subtitle: "Create new livestock batch", icon: "➕", path: "/admin/batches" },
    { title: "Add Livestock", subtitle: "Open batch and add animals", icon: "🐐", path: "/admin/batches" },
    { title: "Record Weight", subtitle: "Update goat growth", icon: "⚖️", path: "/admin/batches" },
    { title: "Disease Case", subtitle: "Record symptoms/treatment", icon: "🩺", path: "/admin/diseases" },
    { title: "Medicine", subtitle: "Plan and execute doses", icon: "💊", path: "/admin/medicines" },
    { title: "Inventory", subtitle: "Feed/medicine stock", icon: "📦", path: "/admin/inventory" },
  ];

  const pieColors = ["#6366f1", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4", "#a855f7", "#f97316"];

  return (
    <>
      <Styles />
      <div className="dash-wrap">
        {loading ? (
          <div className="loader-panel">
            <div className="loader-ring" />
            <div>Loading farm control dashboard...</div>
          </div>
        ) : error ? (
          <div className="error-panel">
            <div style={{ fontSize: 42 }}>⚠️</div>
            <div>{error}</div>
            <button className="dash-btn primary" onClick={loadDashboard}>Retry</button>
          </div>
        ) : (
          <>
            <div className="dash-header">
              <div>
                <h1 className="dash-title">Farm Admin Dashboard</h1>
                <p className="dash-subtitle">
                  Live control room for batches, goats, health, medicine, stock, investors and daily work.
                </p>
              </div>
              <div className="dash-actions">
                <button className="dash-btn" onClick={loadDashboard}>↻ Refresh</button>
                <button className="dash-btn" onClick={() => openDashboardPdf(dashboard, period, filteredBatches)}>⬇ Dashboard PDF</button>
                <button className="dash-btn primary" onClick={() => navigate("/admin/batches")}>+ Manage Batches</button>
              </div>
            </div>

            <div className="kpi-grid">
              {kpis.map((item) => <KpiCard key={item.label} {...item} />)}
            </div>

            <div className="dash-panel" style={{ marginBottom: 18 }}>
              <div className="panel-head">
                <div>
                  <h2 className="panel-title">Quick Actions</h2>
                  <p className="panel-subtitle">Go directly to the daily work pages.</p>
                </div>
              </div>
              <div className="quick-grid">
                {quickActions.map((action) => (
                  <button key={action.title} className="quick-card" onClick={() => navigate(action.path)}>
                    <div className="quick-icon">{action.icon}</div>
                    <p className="quick-title">{action.title}</p>
                    <p className="quick-subtitle">{action.subtitle}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="dash-grid-2">
              <div className="dash-panel">
                <div className="panel-head">
                  <div>
                    <h2 className="panel-title">Batch Performance</h2>
                    <p className="panel-subtitle">Default view is Present batches. Click Past/Future to switch.</p>
                  </div>
                  <div className="period-tabs">
                    {[
                      ["Past", batchCounts.past],
                      ["Present", batchCounts.present],
                      ["Future", batchCounts.future],
                    ].map(([label, count]) => (
                      <button
                        key={label}
                        className={`period-tab ${period === label ? "active" : ""}`}
                        onClick={() => setPeriod(label)}
                      >
                        {label} {number(count)}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="table-scroll">
                  {filteredBatches.length === 0 ? <EmptyState text={`No ${period.toLowerCase()} batches found`} /> : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Batch</th>
                          <th>Status</th>
                          <th>Goats</th>
                          <th>Investment</th>
                          <th>Revenue</th>
                          <th>Profit</th>
                          <th>ROI</th>
                          <th>Progress</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredBatches.slice(0, 8).map((batch) => (
                          <tr key={batch.id}>
                            <td>
                              <div style={{ fontWeight: 900, color: "#fff" }}>{shortName(batch.batchName, 26)}</div>
                              <div style={{ color: "rgba(255,255,255,0.32)", fontSize: 10.5 }}>{dateText(batch.startDate)} → {dateText(batch.endDate)}</div>
                            </td>
                            <td><StatusPill value={batch.status} /></td>
                            <td>{number(batch.goats)}</td>
                            <td>{money(batch.investment)}</td>
                            <td>{money(batch.revenue)}</td>
                            <td style={{ color: Number(batch.profit || 0) >= 0 ? "#22c55e" : "#ef4444", fontWeight: 900 }}>{money(batch.profit)}</td>
                            <td>{pct(batch.roi)}</td>
                            <td>
                              <div className="progress-track"><div className="progress-fill" style={{ width: `${Math.max(0, Math.min(100, Number(batch.progress || 0)))}%` }} /></div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="dash-panel">
                <div className="panel-head">
                  <div>
                    <h2 className="panel-title">Priority Alerts</h2>
                    <p className="panel-subtitle">Problems admin should check first.</p>
                  </div>
                </div>
                <div className="alert-stack">
                  {alerts.length === 0 ? <EmptyState text="No active alerts. Farm looks stable." /> : alerts.slice(0, 7).map((alert, idx) => {
                    const color = alert.priority === "HIGH" ? "#ef4444" : alert.priority === "MEDIUM" ? "#f59e0b" : "#60a5fa";
                    return (
                      <div key={`${alert.title}-${idx}`} className="alert-card" onClick={() => alert.path && navigate(alert.path)}>
                        <span className="alert-dot" style={{ background: color, boxShadow: `0 0 12px ${color}` }} />
                        <div>
                          <p className="alert-title">{alert.title}</p>
                          <p className="alert-msg">{alert.message}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="dash-grid-3">
              <div className="dash-panel">
                <div className="panel-head">
                  <div>
                    <h2 className="panel-title">Today’s Work</h2>
                    <p className="panel-subtitle">Medicine, vaccine and feeding items due today.</p>
                  </div>
                </div>
                {todayTasks.length === 0 ? <EmptyState text="No tasks loaded for today." /> : todayTasks.slice(0, 6).map((task) => (
                  <div className="list-row" key={`${task.type}-${task.id}`}>
                    <div>
                      <p className="list-title">{task.type} • {shortName(task.title, 24)}</p>
                      <p className="list-sub">{task.batchName || "No batch"}{task.asset ? ` • Asset ${task.asset}` : ""}</p>
                    </div>
                    <StatusPill value={task.status} />
                  </div>
                ))}
              </div>

              <div className="dash-panel">
                <div className="panel-head">
                  <div>
                    <h2 className="panel-title">Livestock Health</h2>
                    <p className="panel-subtitle">Health, disease and medicine overview.</p>
                  </div>
                </div>
                <div className="mini-grid">
                  <MiniMetric label="Healthy" value={number(health.healthy)} />
                  <MiniMetric label="Sick" value={number(health.sick)} />
                  <MiniMetric label="Under Treatment" value={number(health.underTreatment)} />
                  <MiniMetric label="Disease Open" value={number(health.openDiseaseCases)} />
                  <MiniMetric label="Medicine Today" value={number(health.medicineDueToday)} />
                  <MiniMetric label="Vaccine Week" value={number(health.vaccineDueThisWeek)} />
                </div>
              </div>

              <div className="dash-panel">
                <div className="panel-head">
                  <div>
                    <h2 className="panel-title">Weight Growth</h2>
                    <p className="panel-subtitle">Investor value depends on animal growth.</p>
                  </div>
                </div>
                <div className="mini-grid">
                  <MiniMetric label="Avg Weight" value={`${Number(weight.averageCurrentWeight || 0).toFixed(1)} kg`} />
                  <MiniMetric label="Total Gain" value={`${Number(weight.totalWeightGain || 0).toFixed(1)} kg`} />
                  <MiniMetric label="Not Updated" value={number(weight.weightNotUpdated)} />
                  <MiniMetric label="Best Gain" value={`${Number(weight.bestGrowthAnimal?.gain || 0).toFixed(1)} kg`} />
                </div>
              </div>
            </div>

            <div className="dash-grid-2">
              <div className="dash-panel">
                <div className="panel-head">
                  <div>
                    <h2 className="panel-title">Batch Investment vs Revenue</h2>
                    <p className="panel-subtitle">Compare money movement batch-wise.</p>
                  </div>
                </div>
                <div className="chart-box">
                  {batchProfitChart.length === 0 ? <EmptyState /> : (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={batchProfitChart} margin={{ top: 12, right: 16, bottom: 12, left: -8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.07)" />
                        <XAxis dataKey="name" stroke="rgba(255,255,255,0.38)" fontSize={10} tickFormatter={(v) => shortName(v, 10)} />
                        <YAxis stroke="rgba(255,255,255,0.38)" fontSize={10} />
                        <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} formatter={(v) => money(v)} />
                        <Legend />
                        <Bar dataKey="investment" name="Investment" fill="#6366f1" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="revenue" name="Revenue" fill="#22c55e" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="profit" name="Profit" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="dash-panel">
                <div className="panel-head">
                  <div>
                    <h2 className="panel-title">Health Status Chart</h2>
                    <p className="panel-subtitle">Animal condition distribution.</p>
                  </div>
                </div>
                <div className="chart-box">
                  {healthChart.length === 0 ? <EmptyState /> : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={healthChart} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={62} outerRadius={104} paddingAngle={2}>
                          {healthChart.map((entry, index) => <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            </div>

            <div className="dash-grid-3">
              <div className="dash-panel">
                <div className="panel-head"><div><h2 className="panel-title">Financial Overview</h2><p className="panel-subtitle">Farm money summary.</p></div></div>
                <div className="mini-grid">
                  <MiniMetric label="Livestock Cost" value={money(financial.livestockCost)} />
                  <MiniMetric label="Labour Cost" value={money(financial.labourCost)} />
                  <MiniMetric label="Sales" value={money(financial.sales)} />
                  <MiniMetric label="Net Profit" value={money(financial.netProfit)} />
                  <MiniMetric label="Expenses" value={money(financial.totalExpenses)} />
                  <MiniMetric label="ROI" value={pct(financial.roi)} />
                </div>
              </div>

              <div className="dash-panel">
                <div className="panel-head"><div><h2 className="panel-title">Inventory Summary</h2><p className="panel-subtitle">Stock items and low stock status.</p></div></div>
                <div className="mini-grid" style={{ marginBottom: 8 }}>
                  <MiniMetric label="Total Items" value={number(inventory.totalItems)} />
                  <MiniMetric label="Low Stock" value={number(inventory.lowStockCount)} />
                </div>
                {(inventory.items || []).slice(0, 4).map((item) => (
                  <div className="list-row" key={item.id}>
                    <div>
                      <p className="list-title">{shortName(item.itemName, 24)}</p>
                      <p className="list-sub">{item.type || "Stock"} • {number(item.quantity)} {item.unit || ""}</p>
                    </div>
                    <StatusPill value={item.status} />
                  </div>
                ))}
              </div>

              <div className="dash-panel">
                <div className="panel-head"><div><h2 className="panel-title">Investor Overview</h2><p className="panel-subtitle">Investor amount and payout visibility.</p></div></div>
                <div className="mini-grid">
                  <MiniMetric label="Investors" value={number(investor.totalInvestors)} />
                  <MiniMetric label="Active Investments" value={number(investor.activeInvestments)} />
                  <MiniMetric label="Investor Amount" value={money(investor.totalInvestorAmount)} />
                  <MiniMetric label="Pending Payout" value={money(investor.pendingPayout)} />
                </div>
                <button className="dash-btn primary" style={{ marginTop: 12, width: "100%", justifyContent: "center" }} onClick={() => navigate("/admin/investors")}>Open Investor Module</button>
              </div>
            </div>

            <div className="dash-grid-2">
              <div className="dash-panel">
                <div className="panel-head"><div><h2 className="panel-title">Task Completion Snapshot</h2><p className="panel-subtitle">Last 7 days care task status.</p></div></div>
                <div className="chart-box">
                  {taskChart.length === 0 ? <EmptyState /> : (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={taskChart} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={105} label>
                          {taskChart.map((entry, index) => <Cell key={`task-${index}`} fill={pieColors[index % pieColors.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ background: "#111827", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" }} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>

              <div className="dash-panel">
                <div className="panel-head"><div><h2 className="panel-title">Recent Activity</h2><p className="panel-subtitle">Latest farm updates.</p></div></div>
                {recentActivities.length === 0 ? <EmptyState text="No recent activity found." /> : recentActivities.slice(0, 8).map((activity, index) => (
                  <div className="list-row" key={`${activity.title}-${index}`}>
                    <div>
                      <p className="list-title">{activity.title}</p>
                      <p className="list-sub">{activity.description}</p>
                    </div>
                    <div style={{ color: "rgba(255,255,255,0.34)", fontSize: 10.5, whiteSpace: "nowrap" }}>{dateText(activity.eventTime)}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
